import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Row, Col } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import messages from '../../../messages';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { TabIds, Tabs } from '../../model';
import { TRACKING_ATTRIBUTES, TAB_IDENTIFIERS } from '../tracking';
import { getDescriptionIndex } from '../utils';

import { ContentTabLink } from './ContentTabLink';
import {
  ContentSwitcherContainer,
  ContentSwitcherWrapper,
  TabList,
  Container,
  ReviewSummaryCount,
} from './styled';

interface IContentSwitcherProps {
  isRichContentAvailable: boolean;
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  variant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
  defaultTab: Tabs;
  productReviewCount: number;
  shouldRenderComposition: boolean;
  shouldRenderOthersTab: boolean;
}

export const ContentSwitcher: React.FC<IContentSwitcherProps> = ({
  product,
  variant,
  defaultTab,
  isRichContentAvailable,
  productReviewCount,
  shouldRenderComposition,
  shouldRenderOthersTab,
}) => {
  const { bazaarvoiceIntegrationPd } = useFeatureFlags();
  const newPdDesignEnabled = useNewPdDesignEnabled();
  const descriptionIndex = getDescriptionIndex({
    description: product.description,
    composition: product.features.composition,
    variant,
  });

  const { brand, description } = product;

  const shouldRenderAboutBrand = Boolean(brand?.description);

  return (
    <ContentSwitcherContainer newPdDesignEnabled={newPdDesignEnabled}>
      <ContentSwitcherWrapper>
        <Container newPdDesignEnabled={newPdDesignEnabled}>
          <Row>
            <Col>
              <TabList role="tablist">
                {descriptionIndex && (
                  <ContentTabLink
                    tabId={TabIds.Description}
                    ariaControls={TAB_IDENTIFIERS.description}
                    trackingLabel={TRACKING_ATTRIBUTES.description}
                    isDefaultTab={defaultTab === Tabs.Description}
                    id={description ? 'tabDescription' : 'tabComposition'}
                  >
                    <FormattedMessage
                      {...messages[descriptionIndex]}
                      values={{ name: '' }}
                    />
                  </ContentTabLink>
                )}

                {shouldRenderComposition && (
                  <ContentTabLink
                    tabId={TabIds.Composition}
                    ariaControls={TAB_IDENTIFIERS.composition}
                    trackingLabel={TRACKING_ATTRIBUTES.composition}
                    id="tabComposition"
                  >
                    <FormattedMessage {...messages.compositionTab} />
                  </ContentTabLink>
                )}

                {shouldRenderOthersTab && (
                  <ContentTabLink
                    tabId={TabIds.Other}
                    ariaControls={TAB_IDENTIFIERS.other}
                    trackingLabel={TRACKING_ATTRIBUTES.other}
                    id="tabOther"
                  >
                    <FormattedMessage {...messages.other} />
                  </ContentTabLink>
                )}

                {shouldRenderAboutBrand && (
                  <ContentTabLink
                    tabId={TabIds.AboutBrand}
                    ariaControls={TAB_IDENTIFIERS.brand}
                    trackingLabel={TRACKING_ATTRIBUTES.brand}
                    isDefaultTab={defaultTab === Tabs.AboutBrand}
                    id="tabBrand"
                  >
                    <FormattedMessage
                      {...messages.aboutBrand}
                      values={{ brandName: '' }}
                    />
                  </ContentTabLink>
                )}

                {isRichContentAvailable && (
                  <ContentTabLink
                    tabId={TabIds.RichContent}
                    ariaControls={TAB_IDENTIFIERS.richContent}
                    trackingLabel={TRACKING_ATTRIBUTES.richContent}
                    id="tabRichContent"
                  >
                    <FormattedMessage {...messages.fixedHeaderRichContent} />
                  </ContentTabLink>
                )}

                <ContentTabLink
                  tabId={TabIds.Reviews}
                  ariaControls={TAB_IDENTIFIERS.reviews}
                  id="tabReviews"
                  trackingLabel={TRACKING_ATTRIBUTES.reviews}
                >
                  <FormattedMessage
                    {...messages.reviewHeader}
                    values={{
                      name: (
                        <ReviewSummaryCount id="tabReviews_reviewCount">
                          {productReviewCount > 0 && !bazaarvoiceIntegrationPd
                            ? productReviewCount
                            : ''}
                        </ReviewSummaryCount>
                      ),
                    }}
                  />
                </ContentTabLink>
              </TabList>
            </Col>
          </Row>
        </Container>
      </ContentSwitcherWrapper>
    </ContentSwitcherContainer>
  );
};
